// SuggestionMark.ts
//
// A single TipTap mark representing one tracked-change suggestion on a text
// range. `type` distinguishes a pending insertion (the text is really in the
// doc but visually pending) from a pending deletion (the text is retained but
// struck through). The mark lives in the Yjs doc, so it syncs live and the
// ws-server can detect it to keep `.draft` accepted-only.
//
// NOTE: the ws-server keeps a matching minimal `suggestion` mark in its
// markdownExtensions schema (ws-server/tiptapHelper.js) so the ydoc->prosemirror
// conversion preserves the mark before the accepted-text transform. Keep the
// mark NAME and ATTRS in sync across both.
import { Mark, mergeAttributes } from "@tiptap/core";

export type SuggestionType = "insert" | "delete";

export interface SuggestionMarkAttrs {
  id: string;
  authorId: string;
  authorName: string;
  type: SuggestionType;
  color: string;
}

const attr = (name: string, fallback: unknown = null) => ({
  default: fallback,
  parseHTML: (el: HTMLElement) => el.getAttribute(name) ?? fallback,
  renderHTML: (a: Record<string, unknown>) => {
    const key = name;
    const val = a[
      name === "data-suggestion-id" ? "id"
      : name === "data-suggestion-author-id" ? "authorId"
      : name === "data-suggestion-author-name" ? "authorName"
      : name === "data-suggestion-type" ? "type"
      : "color"
    ];
    return val == null || val === "" ? {} : { [key]: String(val) };
  },
});

export const SuggestionMark = Mark.create({
  name: "suggestion",
  // Extend the mark when typing at its boundary so a run of keystrokes stays one
  // suggestion (the interceptor also reuses an adjacent same-author insert id).
  inclusive: true,
  // Suggestions are excludable from copy and never auto-merge across distinct ids.
  spanning: true,

  addAttributes() {
    return {
      id: attr("data-suggestion-id"),
      authorId: attr("data-suggestion-author-id"),
      authorName: attr("data-suggestion-author-name", ""),
      type: attr("data-suggestion-type", "insert"),
      color: attr("data-suggestion-color", "#2c1561"),
    };
  },

  parseHTML() {
    return [{ tag: "span[data-suggestion-id]" }];
  },

  renderHTML({ HTMLAttributes }) {
    const type = (HTMLAttributes["data-suggestion-type"] as string) || "insert";
    const color = (HTMLAttributes["data-suggestion-color"] as string) || "#2c1561";
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        class: `suggestion suggestion-${type}`,
        style: `--sugg-color:${color}`,
      }),
      0,
    ];
  },
});
